import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth, useTheme } from '@/App';
import DashboardLayout from '@/components/layout/DashboardLayout';
import DrishtiPaywallGate from '@/components/drishti/DrishtiPaywallGate';
import {
  Wallet, Activity, TrendingUp, TrendingDown, RefreshCw,
  LogIn, Unplug, CheckCircle2, BarChart2,
  Briefcase, Zap, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConnectDhanModal } from '@/components/dhan/DhanModals';
import { toast } from 'sonner';

const fmt = (n, digits = 2) =>
  Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });

const PnLBadge = ({ value }) => {
  const isPos = value >= 0;
  return (
    <span className={`font-bold font-jetbrains ${isPos ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
      {isPos ? '+' : ''}₹{fmt(Math.abs(value))}
    </span>
  );
};

const TABS = ['Overview', 'Positions', 'Drishti Trades', 'Holdings'];

const DhanPortfolioPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [hasAccess, setHasAccess] = useState(Boolean(isAdmin || user?.has_drishti_access === true));

  const [dhanStatus, setDhanStatus] = useState(null);
  const [funds, setFunds] = useState(null);
  const [positions, setPositions] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [drishtiOrders, setDrishtiOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('Overview');

  useEffect(() => {
    if (isAdmin) {
      setHasAccess(true);
      return;
    }
    axios.get(`${API}/drishti/subscription-status`, { withCredentials: true })
      .then(res => {
        setHasAccess(Boolean(res.data?.has_access));
      })
      .catch(() => {
        setHasAccess(false);
      });
  }, [isAdmin, user]);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/trading/dhan-status`, { withCredentials: true });
      setDhanStatus(res.data);
      return res.data.connected;
    } catch {
      setDhanStatus({ connected: false });
      return false;
    }
  };

  const fetchAll = async () => {
    const [fundsRes, posRes, holdRes, ordersRes] = await Promise.allSettled([
      axios.get(`${API}/trading/fund-limits`, { withCredentials: true }),
      axios.get(`${API}/trading/positions`, { withCredentials: true }),
      axios.get(`${API}/trading/holdings`, { withCredentials: true }),
      axios.get(`${API}/trading/orders`, { withCredentials: true }),
    ]);
    if (fundsRes.status === 'fulfilled') setFunds(fundsRes.value.data);
    if (posRes.status === 'fulfilled') setPositions(posRes.value.data?.data || []);
    if (holdRes.status === 'fulfilled') setHoldings(holdRes.value.data?.data || []);
    if (ordersRes.status === 'fulfilled') setDrishtiOrders(ordersRes.value.data?.data || []);
  };

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    const connected = await checkStatus();
    if (connected) await fetchAll();
    setLoading(false); setRefreshing(false);
  }, []);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API}/trading/disconnect-dhan`, {}, { withCredentials: true });
      toast.success('Dhan account disconnected');
      setDhanStatus({ connected: false });
      setFunds(null); setPositions([]); setHoldings([]); setDrishtiOrders([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    } finally { setDisconnecting(false); }
  };

  useEffect(() => { 
    if (hasAccess || isAdmin) {
      loadAll(); 
    }
  }, [loadAll, hasAccess, isAdmin]);

  if (!hasAccess && !isAdmin) {
    return (
      <DashboardLayout title="My Dhan Portfolio">
        <DrishtiPaywallGate
          featureName="My Dhan Live Terminal & Execution"
          onAccessGranted={() => {
            setHasAccess(true);
            loadAll();
          }}
        />
      </DashboardLayout>
    );
  }

  const totalRealized = positions.reduce((s, p) => s + (p.realizedProfit || 0), 0);
  const totalUnrealized = positions.reduce((s, p) => s + (p.unrealizedProfit || 0), 0);
  const todayPnl = totalRealized + totalUnrealized;
  const availableCapital = funds?.raw?.availabelBalance ?? funds?.raw?.availableBalance ?? funds?.available ?? 0;
  const openPositions = positions.filter(p => p.netQty !== 0);
  const closedToday = positions.filter(p => p.netQty === 0);

  if (loading) return (
    <DashboardLayout title="My Dhan">
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-10 h-10 animate-spin text-[#00FF88]" />
          <p className="font-jetbrains text-sm text-gray-400 dark:text-[#52525B]">Syncing with Dhan…</p>
        </div>
      </div>
    </DashboardLayout>
  );

  if (!dhanStatus?.connected) return (
    <DashboardLayout title="My Dhan">
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
        <div className="w-24 h-24 rounded-full bg-gray-50 dark:bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] flex items-center justify-center mb-6">
          <Wallet className="w-12 h-12 text-gray-400 dark:text-[#52525B]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit mb-2">Connect Your Dhan Account</h2>
        <p className="text-gray-500 dark:text-[#94A3B8] font-plex max-w-md mb-8 text-sm leading-relaxed">
          Link your Dhan trading account to see live balance, positions, P&L, Drishti AI trades, and holdings — all in one powerful dashboard.
        </p>
        <Button onClick={() => setConnectModalOpen(true)} className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-bold px-10 h-11 text-base">
          <LogIn className="w-4 h-4 mr-2" /> Connect Dhan Account
        </Button>
      </div>
      <ConnectDhanModal open={connectModalOpen} onOpenChange={setConnectModalOpen} onConnected={() => { setConnectModalOpen(false); loadAll(); }} />
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="My Dhan">
      <div className="max-w-7xl mx-auto space-y-5 pb-8">

        {/* Top Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Available Capital', value: availableCapital, icon: Wallet, color: '#00F0FF', prefix: '₹', alwaysPositive: true },
            { label: "Today's P&L", value: todayPnl, icon: todayPnl >= 0 ? TrendingUp : TrendingDown, color: todayPnl >= 0 ? '#00FF88' : '#FF3B30' },
            { label: 'Realized', value: totalRealized, icon: BarChart2, color: '#FFCC00' },
            { label: 'Unrealized', value: totalUnrealized, icon: Activity, color: '#A855F7' },
          ].map(({ label, value, icon: Icon, color, alwaysPositive }) => {
            const displayColor = isLight
              ? color === '#00F0FF' ? '#0284C7'
                : color === '#00FF88' ? '#059669'
                : color === '#FFCC00' ? '#D97706'
                : color === '#A855F7' ? '#7C3AED'
                : color === '#FF3B30' ? '#DC2626'
                : color
              : color;
            const pnlColor = isLight
              ? (value >= 0 ? '#059669' : '#DC2626')
              : (value >= 0 ? '#00FF88' : '#FF3B30');

            return (
              <div key={label} className={`border rounded-xl p-5 relative overflow-hidden group hover:border-opacity-50 transition-all shadow-sm ${isLight ? 'bg-white border-slate-200' : 'bg-[#0D1117] border-[#1E293B]'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4" style={{ color: displayColor }} />
                  <p className={`text-[10px] font-jetbrains uppercase tracking-widest font-bold ${isLight ? 'text-slate-600' : 'text-[#52525B]'}`}>{label}</p>
                </div>
                <p className="text-2xl lg:text-3xl font-bold font-jetbrains" style={{ color: alwaysPositive ? displayColor : pnlColor }}>
                  {!alwaysPositive && value >= 0 ? '+' : !alwaysPositive && value < 0 ? '-' : ''}₹{fmt(Math.abs(value))}
                </p>
              </div>
            );
          })}
        </div>

        {/* Tabbed Panel */}
        <div className={`border rounded-xl overflow-hidden shadow-sm ${isLight ? 'bg-white border-slate-200' : 'bg-[#0D1117] border-[#1E293B]'}`}>
          <div className={`flex items-center gap-1 border-b px-4 pt-3 overflow-x-auto ${isLight ? 'border-slate-200 bg-slate-50/50' : 'border-[#1E293B]'}`}>
            {TABS.map(tab => {
              const counts = { 'Positions': openPositions.length, 'Drishti Trades': drishtiOrders.length, 'Holdings': holdings.length };
              const tabColors = { 'Positions': isLight ? '#0284C7' : '#00F0FF', 'Drishti Trades': isLight ? '#059669' : '#00FF88', 'Holdings': isLight ? '#7C3AED' : '#A855F7' };
              const icons = { 'Overview': Activity, 'Positions': BarChart2, 'Drishti Trades': Zap, 'Holdings': Briefcase };
              const TabIcon = icons[tab];
              return (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-outfit font-medium transition-all whitespace-nowrap border-b-2 -mb-px
                    ${activeTab === tab 
                      ? (isLight ? 'text-slate-900 border-sky-600 font-bold' : 'text-white border-[#00F0FF]') 
                      : (isLight ? 'text-slate-500 border-transparent hover:text-slate-900' : 'text-[#52525B] border-transparent hover:text-[#94A3B8]')}`}>
                  <TabIcon className="w-3.5 h-3.5" /> {tab}
                  {counts[tab] > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-jetbrains font-bold" style={{ background: `${tabColors[tab]}18`, color: tabColors[tab] }}>
                      {counts[tab]}
                    </span>
                  )}
                </button>
              );
            })}
            <div className="ml-auto pb-2">
              <Button onClick={() => loadAll(true)} variant="ghost" size="sm" className={`h-8 text-xs ${isLight ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' : 'text-[#52525B] hover:text-white'}`}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'Overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-5">
              <div>
                <h3 className={`text-sm font-bold font-outfit mb-3 flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  <ArrowUpRight className="w-4 h-4 text-[#00FF88]" /> Open Positions ({openPositions.length})
                </h3>
                {openPositions.length === 0
                  ? <p className={`text-sm font-jetbrains py-4 ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>No open positions</p>
                  : <div className="space-y-2">
                    {openPositions.slice(0, 6).map((p, i) => {
                      const pl = (p.realizedProfit || 0) + (p.unrealizedProfit || 0);
                      return (
                        <div key={i} className={`flex items-center justify-between border rounded-lg px-4 py-3 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                          <div>
                            <p className={`font-bold font-jetbrains text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>{p.tradingSymbol}</p>
                            <p className={`text-[10px] font-jetbrains uppercase ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>Qty: {p.netQty} · {p.productType}</p>
                          </div>
                          <div className="text-right">
                            <PnLBadge value={pl} />
                            <p className={`text-[10px] font-jetbrains mt-0.5 ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>LTP: {p.lastPrice ? `₹${fmt(p.lastPrice)}` : '—'}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>}
              </div>
              <div>
                <h3 className={`text-sm font-bold font-outfit mb-3 flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" /> Closed Today ({closedToday.length})
                </h3>
                {closedToday.length === 0
                  ? <p className={`text-sm font-jetbrains py-4 ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>No closed trades today</p>
                  : <div className="space-y-2">
                    {closedToday.slice(0, 6).map((p, i) => (
                      <div key={i} className={`flex items-center justify-between border rounded-lg px-4 py-3 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                        <div>
                          <p className={`font-bold font-jetbrains text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>{p.tradingSymbol}</p>
                          <p className={`text-[10px] font-jetbrains uppercase ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{p.productType}</p>
                        </div>
                        <PnLBadge value={p.realizedProfit || 0} />
                      </div>
                    ))}
                  </div>}
              </div>
            </div>
          )}

          {/* Positions Tab */}
          {activeTab === 'Positions' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`border-b ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                    {['Symbol', 'Product', 'Qty', 'Avg Price', 'LTP', 'Realized', 'Unrealized', 'P&L'].map(h => (
                      <th key={h} className={`p-4 text-[10px] font-jetbrains uppercase font-semibold whitespace-nowrap ${isLight ? 'text-slate-600' : 'text-gray-400 dark:text-[#52525B]'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.length > 0 ? positions.map((p, idx) => {
                    const pl = (p.realizedProfit || 0) + (p.unrealizedProfit || 0);
                    return (
                      <tr key={idx} className={`border-b transition-colors ${isLight ? 'border-slate-100 hover:bg-slate-50' : 'border-white/[0.03] hover:bg-white/[0.01]'}`}>
                        <td className="p-4"><p className={`font-bold font-jetbrains text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>{p.tradingSymbol}</p><p className={`text-[10px] font-jetbrains uppercase ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{p.exchangeSegment}</p></td>
                        <td className="p-4"><span className={`px-2 py-0.5 rounded text-[10px] font-jetbrains ${p.productType === 'CNC' ? (isLight ? 'bg-emerald-50 text-emerald-700 font-bold border border-emerald-200' : 'bg-[#00FF88]/10 text-[#00FF88]') : (isLight ? 'bg-slate-100 text-slate-700' : 'bg-white/5 text-[#94A3B8]')}`}>{p.productType}</span></td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>{p.netQty}</td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-600' : 'text-gray-500 dark:text-[#94A3B8]'}`}>₹{fmt(p.buyAvg || p.sellAvg)}</td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>{p.lastPrice ? `₹${fmt(p.lastPrice)}` : '—'}</td>
                        <td className="p-4"><PnLBadge value={p.realizedProfit || 0} /></td>
                        <td className="p-4"><PnLBadge value={p.unrealizedProfit || 0} /></td>
                        <td className={`p-4 font-jetbrains text-sm font-bold ${pl >= 0 ? (isLight ? 'text-emerald-700' : 'text-[#00FF88]') : (isLight ? 'text-rose-700' : 'text-[#FF3B30]')}`}>{pl >= 0 ? '+' : ''}₹{fmt(Math.abs(pl))}</td>
                      </tr>
                    );
                  }) : <tr><td colSpan={8} className={`p-12 text-center font-jetbrains text-sm ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>No positions found</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* Drishti Trades Tab */}
          {activeTab === 'Drishti Trades' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`border-b ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                    {['Date & Time', 'Symbol', 'Action', 'Qty', 'Price', 'Target', 'Stop Loss', 'Status'].map(h => (
                      <th key={h} className={`p-4 text-[10px] font-jetbrains uppercase font-semibold whitespace-nowrap ${isLight ? 'text-slate-600' : 'text-gray-400 dark:text-[#52525B]'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drishtiOrders.length > 0 ? drishtiOrders.map((o, idx) => (
                    <tr key={idx} className={`border-b transition-colors ${isLight ? 'border-slate-100 hover:bg-slate-50' : 'border-white/[0.03] hover:bg-white/[0.01]'}`}>
                      <td className={`p-4 font-jetbrains text-xs whitespace-nowrap ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                      <td className="p-4"><p className={`font-bold font-jetbrains text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>{o.trading_symbol || o.symbol || '—'}</p><p className={`text-[10px] font-jetbrains uppercase ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{o.exchange_segment || ''}</p></td>
                      <td className="p-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold font-jetbrains ${o.transaction_type === 'BUY' ? (isLight ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-[#00FF88]/10 text-[#00FF88]') : (isLight ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-[#FF3B30]/10 text-[#FF3B30]')}`}>{o.transaction_type || '—'}</span></td>
                      <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>{o.quantity || '—'}</td>
                      <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>₹{fmt(o.price)}</td>
                      <td className={`p-4 font-jetbrains text-sm font-semibold ${isLight ? 'text-emerald-700' : 'text-[#00FF88]'}`}>₹{fmt(o.target)}</td>
                      <td className={`p-4 font-jetbrains text-sm font-semibold ${isLight ? 'text-rose-700' : 'text-[#FF3B30]'}`}>₹{fmt(o.stop_loss)}</td>
                      <td className="p-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold font-jetbrains ${o.status === 'success' ? (isLight ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-[#00FF88]/10 text-[#00FF88]') : o.status === 'failure' ? (isLight ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-[#FF3B30]/10 text-[#FF3B30]') : (isLight ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-[#FFCC00]/10 text-[#FFCC00]')}`}>{(o.status || 'pending').toUpperCase()}</span></td>
                    </tr>
                  )) : <tr><td colSpan={8} className={`p-12 text-center font-jetbrains text-sm ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>No Drishti trades found. Use Trade Setup to place your first order!</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* Holdings Tab */}
          {activeTab === 'Holdings' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`border-b ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                    {['Symbol', 'Qty', 'Avg Cost', 'LTP', 'Current Value', 'P&L', 'P&L %'].map(h => (
                      <th key={h} className={`p-4 text-[10px] font-jetbrains uppercase font-semibold whitespace-nowrap ${isLight ? 'text-slate-600' : 'text-gray-400 dark:text-[#52525B]'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {holdings.length > 0 ? holdings.map((h, idx) => {
                    const pl = (h.totalQty || 0) * ((h.lastTradedPrice || 0) - (h.avgCostPrice || 0));
                    const plPct = h.avgCostPrice ? (pl / ((h.avgCostPrice || 1) * (h.totalQty || 1))) * 100 : 0;
                    return (
                      <tr key={idx} className={`border-b transition-colors ${isLight ? 'border-slate-100 hover:bg-slate-50' : 'border-white/[0.03] hover:bg-white/[0.01]'}`}>
                        <td className="p-4"><p className={`font-bold font-jetbrains text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>{h.tradingSymbol}</p><p className={`text-[10px] font-jetbrains uppercase ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>{h.exchangeSegment}</p></td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>{h.totalQty}</td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-600' : 'text-gray-500 dark:text-[#94A3B8]'}`}>₹{fmt(h.avgCostPrice)}</td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>₹{fmt(h.lastTradedPrice)}</td>
                        <td className={`p-4 font-jetbrains text-sm ${isLight ? 'text-slate-900 font-semibold' : 'text-white'}`}>₹{fmt((h.totalQty || 0) * (h.lastTradedPrice || 0))}</td>
                        <td className={`p-4 font-jetbrains text-sm font-bold ${pl >= 0 ? (isLight ? 'text-emerald-700' : 'text-[#00FF88]') : (isLight ? 'text-rose-700' : 'text-[#FF3B30]')}`}>{pl >= 0 ? '+' : ''}₹{fmt(Math.abs(pl))}</td>
                        <td className={`p-4 font-jetbrains text-sm font-bold ${plPct >= 0 ? (isLight ? 'text-emerald-700' : 'text-[#00FF88]') : (isLight ? 'text-rose-700' : 'text-[#FF3B30]')}`}>{plPct >= 0 ? '+' : ''}{fmt(Math.abs(plPct), 1)}%</td>
                      </tr>
                    );
                  }) : <tr><td colSpan={7} className={`p-12 text-center font-jetbrains text-sm ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>No holdings found</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom Connection Bar */}
        <div className={`border rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 shadow-sm ${isLight ? 'bg-white border-slate-200' : 'bg-[#0D1117] border-[#1E293B]'}`}>
          <div className="flex items-center gap-3">
            <CheckCircle2 className={`w-5 h-5 ${isLight ? 'text-emerald-600' : 'text-[#00FF88]'}`} />
            <div>
              <p className={`text-sm font-bold font-outfit flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                Dhan Trading Account
                <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${isLight ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/20 shadow-[0_0_10px_rgba(0,255,136,0.1)]'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isLight ? 'bg-emerald-600' : 'bg-[#00FF88]'} animate-pulse`} /> Connected ({dhanStatus?.client_id})
                </span>
              </p>
              <p className={`text-xs font-jetbrains mt-0.5 ${isLight ? 'text-slate-500' : 'text-gray-400 dark:text-[#52525B]'}`}>
                {dhanStatus?.connected_at ? `Connected since ${new Date(dhanStatus.connected_at).toLocaleDateString('en-IN')}` : ''}
                {dhanStatus?.hours_remaining ? ` · Expires in ${dhanStatus.hours_remaining}h` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setConnectModalOpen(true)} variant="outline" className={`h-8 text-xs font-medium ${isLight ? 'border-slate-300 text-slate-700 bg-white hover:bg-slate-100' : 'border-[#1E293B] text-white hover:bg-white/5'}`}>
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Update Token
            </Button>
            <Button onClick={handleDisconnect} disabled={disconnecting} variant="outline" className={`h-8 text-xs font-medium ${isLight ? 'border-rose-300 text-rose-600 bg-white hover:bg-rose-50' : 'border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10'}`}>
              <Unplug className="w-3.5 h-3.5 mr-1.5" /> {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </div>
        </div>

      </div>

      <ConnectDhanModal open={connectModalOpen} onOpenChange={setConnectModalOpen} onConnected={() => { setConnectModalOpen(false); loadAll(); }} />
    </DashboardLayout>
  );
};

export default DhanPortfolioPage;
